import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
const { FiDollarSign, FiFlag, FiCreditCard, FiEdit, FiFilter, FiX, FiAlertTriangle, FiPlusCircle, FiEye, FiMinus, FiSearch, FiUser, FiPrinter, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } = FiIcons;
import InvoicePrint from '../components/InvoicePrint';


// SearchableDropdown Component for Treatment Codes
const SearchableDropdown = ({ value, onChange, onSelect, placeholder = "Search treatments...", className = "", treatmentCodes = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredDescription, setHoveredDescription] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter treatment codes based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(treatmentCodes.slice(0, 20));// Show first 20 when no search
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      const filtered = treatmentCodes.filter(treatment =>
        treatment.code.toLowerCase().includes(searchTermLower) ||
        treatment.description.toLowerCase().includes(searchTermLower)
      ).slice(0, 50);// Limit to 50 results
      setFilteredOptions(filtered);
    }
  }, [searchTerm, treatmentCodes]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleOptionSelect = (treatment) => {
    setSearchTerm(treatment.code);
    setIsOpen(false);
    onSelect(treatment);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleDescriptionMouseEnter = (description, event) => {
    // Only show tooltip if description is longer than 50 characters
    if (description.length > 50) {
      const rect = event.target.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
      setHoveredDescription(description);
      setShowTooltip(true);
    }
  };

  const handleDescriptionMouseLeave = () => {
    setShowTooltip(false);
    setHoveredDescription('');
  };

  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className="w-full px-3 py-2 pr-10 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-grey-400" />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-grey-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-grey-500">Loading...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((treatment) => (
                <div
                  key={treatment.id}
                  onClick={() => handleOptionSelect(treatment)}
                  className="p-3 hover:bg-grey-100 cursor-pointer border-b border-grey-100 last:border-b-0"
                >
                  <div className="font-medium text-grey-900">
                    {treatment.code}
                  </div>
                  <div
                    className="text-sm text-grey-600 cursor-help"
                    onMouseEnter={(e) => handleDescriptionMouseEnter(treatment.description, e)}
                    onMouseLeave={handleDescriptionMouseLeave}
                  >
                    {treatment.description.length > 50
                      ? `${treatment.description.substring(0, 50)}...`
                      : treatment.description}
                  </div>
                  <div className="text-sm text-grey-600 mt-1">
                    QR {parseFloat(treatment.default_cost).toFixed(2)}
                    {treatment.is_specialist && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Specialist
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-grey-500">
                {searchTerm.trim() ? 'No treatments found' : 'Start typing to search treatments'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && hoveredDescription && (
        <div
          className="fixed z-50 bg-black text-white text-sm px-3 py-2 rounded-lg shadow-lg max-w-xs break-words pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
          }}
        >
          <div className="relative">
            {hoveredDescription}
            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
          </div>
        </div>
      )}
    </>
  );
};

const Invoices = () => {
  const [allInvoices, setAllInvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [treatmentCodes, setTreatmentCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'cash',
    notes: ''
  });
  const [deductionData, setDeductionData] = useState({
    deduction_amount: 0,
    reason: ''
  });

  const handleDeleteDeduction = async (deductionId) => {
    if (!window.confirm('Are you sure you want to delete this deduction? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/invoices/${selectedInvoice.id}/deductions/${deductionId}`);
      
      // If the invoice has a doctor payout, refresh the entire invoice to get updated payout info
      if (selectedInvoice.doctor_payout) {
        const { data: updatedInvoice } = await axios.get(`/api/invoices/${selectedInvoice.id}`);
        setSelectedInvoice(updatedInvoice);
      } else {
        // Just update the deductions list if there's no doctor payout to update
        setSelectedInvoice(prev => ({
          ...prev,
          manual_deductions: prev.manual_deductions.filter(d => d.id !== deductionId)
        }));
      }
      
      // Show success message
      alert('Deduction deleted successfully');
    } catch (error) {
      console.error('Error deleting deduction:', error);
      alert(`Failed to delete deduction: ${error.response?.data?.error || error.message}`);
    }
  };
  const [formData, setFormData] = useState({
    patient_id: '',
    appointment_id: '',
    treatments: [{
      treatment_code: '',
      description: '',
      cost: 0,
      is_specialist: false
    }],
    payment_method: 'cash',
    paid_amount: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    include_flagged: false
  });
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isDoctorSpecialist, setIsDoctorSpecialist] = useState(false);

  // Live patient search state
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 20,
    totalItems: 0
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const queryParams = new URLSearchParams(location.search);

  // Format Patient ID with leading zeroes
  const formatPatientId = (id) => {
    return String(id).padStart(6, '0');
  };

  // Check if invoice number should be hidden for current user role
  const shouldHideInvoiceNumber = () => {
    return user?.role === 'receptionist' || user?.role === 'manager';
  };

  useEffect(() => {
    // Check if we're opening a new invoice from appointments
    const isNew = queryParams.get('new');
    const appointmentId = queryParams.get('appointment_id');
    const patientId = queryParams.get('patient_id');

    if (isNew === 'true') {
      const newFormData = { ...formData };
      if (patientId) {
        newFormData.patient_id = patientId;
      }
      if (appointmentId) {
        newFormData.appointment_id = appointmentId;
      }
      setFormData(newFormData);
      setShowModal(true);
    }

    fetchInvoices();
    fetchPatients();
    fetchAppointments();
    fetchTreatmentCodes();
  }, [location.search]);

  useEffect(() => {
    fetchInvoices();
  }, [filters]);

  // FIXED: Separate the search filtering from pagination
  // This effect handles the search filtering
  useEffect(() => {
    if (patientSearchTerm.trim() === '') {
      setFilteredInvoices(allInvoices);
    } else {
      const searchTermLower = patientSearchTerm.toLowerCase();
      const filtered = allInvoices.filter(invoice => {
        // Search in patient name
        if (invoice.patient_name && invoice.patient_name.toLowerCase().includes(searchTermLower)) {
          return true;
        }

        // Find the patient record to search in additional patient fields
        const patient = patients.find(p => p.full_name === invoice.patient_name);
        if (patient) {
          // Search in patient contact number
          if (patient.contact_number && patient.contact_number.toLowerCase().includes(searchTermLower)) {
            return true;
          }
          // Search in patient email
          if (patient.email && patient.email.toLowerCase().includes(searchTermLower)) {
            return true;
          }
          // Search in patient national ID (QID)
          if (patient.national_id && patient.national_id.toLowerCase().includes(searchTermLower)) {
            return true;
          }
          // Search by Patient ID (formatted)
          if (formatPatientId(patient.id).includes(patientSearchTerm)) {
            return true;
          }
          // Search by raw Patient ID
          if (patient.id.toString().includes(patientSearchTerm)) {
            return true;
          }
        }

        return false;
      });
      setFilteredInvoices(filtered);
    }

    // FIXED: Reset to first page when search term changes,but don't reset pagination when just changing pages
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalPages: Math.max(1, Math.ceil((patientSearchTerm.trim() === '' ? allInvoices.length : filteredInvoices.length) / prev.itemsPerPage)),
      totalItems: patientSearchTerm.trim() === '' ? allInvoices.length : filteredInvoices.length
    }));
  }, [patientSearchTerm, allInvoices, patients]);

  // FIXED: This effect handles pagination of the already filtered data
  useEffect(() => {
    updatePaginatedData();
  }, [pagination.currentPage, filteredInvoices]);

  const updatePaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;

    const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);
    setInvoices(paginatedInvoices);

    // FIXED: Update pagination info without changing current page unless necessary
    const totalItems = filteredInvoices.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.itemsPerPage));

    setPagination(prev => ({
      ...prev,
      totalPages,
      totalItems,
      // Only reset current page if it's greater than total pages
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPage
      }));

      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearSearch = () => {
    setPatientSearchTerm('');
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();

      // Only include flagged invoices if user is admin AND specifically requests them
      if (user?.role === 'admin' && filters.include_flagged) {
        params.append('include_flagged', 'true');
      }

      if (filters.status) {
        params.append('status', filters.status);
      }

      const response = await axios.get(`/api/invoices?${params.toString()}`);

      let invoicesData = [];
      if (response.data.invoices && Array.isArray(response.data.invoices)) {
        invoicesData = response.data.invoices;
      } else if (Array.isArray(response.data)) {
        invoicesData = response.data;
      }

      // Store all invoices for search and filtering
      setAllInvoices(invoicesData);
      setFilteredInvoices(invoicesData);

      // Update pagination info based on all data
      const totalItems = invoicesData.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / pagination.itemsPerPage));

      setPagination(prev => ({
        ...prev,
        totalPages,
        totalItems,
        currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
      }));

    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
      setAllInvoices([]);
      setFilteredInvoices([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalItems: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get('/api/patients');
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  // OPTIMIZED: Fetch all appointments with invoice status in a single query
  const fetchAppointments = async () => {
    try {
      // Use the optimized appointments endpoint that includes invoice status
      const response = await axios.get('/api/appointments?uninvoiced=true');

      // The server already returns appointments with hasActiveInvoice field
      // Filter to only show completed appointments without active invoices
      const availableAppointments = response.data.filter(appointment =>
        appointment.status === 'completed' && !appointment.hasActiveInvoice
      );
      setAppointments(availableAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchTreatmentCodes = async () => {
    try {
      const response = await axios.get('/api/settings/treatment-codes');
      console.log('Raw treatment codes from API:', response.data);

      // Filter only active treatment codes and ensure proper data types
      const activeCodes = response.data
        .filter(code => code.active)
        .map(code => ({
          ...code,
          // Ensure default_cost is properly formatted as a number
          default_cost: parseFloat(code.default_cost) || 0,
          // Ensure is_specialist is a boolean
          is_specialist: Boolean(code.is_specialist)
        }));

      console.log('Processed treatment codes:', activeCodes);
      setTreatmentCodes(activeCodes);
    } catch (error) {
      console.error('Error fetching treatment codes:', error);
    }
  };

  // Fetch appointment details when an appointment is selected
  const fetchAppointmentDetails = async (appointmentId) => {
    try {
      const response = await axios.get(`/api/appointments/${appointmentId}`);
      const appointment = response.data;

      if (appointment) {
        // Check if the doctor is a specialist
        if (appointment.doctor_id) {
          try {
            const doctorResponse = await axios.get(`/api/settings/doctors/${appointment.doctor_id}`);
            setSelectedDoctor(doctorResponse.data);
            setIsDoctorSpecialist(doctorResponse.data.is_specialist || false);
          } catch (error) {
            console.error('Error fetching doctor details:', error);
            setIsDoctorSpecialist(false);
          }
        }

        // Pre-populate treatment based on appointment details
        const newTreatments = appointment.treatments && appointment.treatments.length > 0
          ? appointment.treatments.map(treatment => ({
            treatment_code: treatment.treatment_code || '',
            description: treatment.description || '',
            cost: treatment.cost || 0,
            is_specialist: false
          }))
          : [{
            treatment_code: '',
            description: '',
            cost: 0,
            is_specialist: false
          }];

        setFormData(prev => ({
          ...prev,
          patient_id: appointment.patient_id,
          treatments: newTreatments
        }));
      }
    } catch (error) {
      console.error('Error fetching appointment details:', error);
    }
  };

  useEffect(() => {
    if (formData.appointment_id) {
      fetchAppointmentDetails(formData.appointment_id);
    }
  }, [formData.appointment_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Fix: Ensure appointment_id is null if it's an empty string
      const dataToSubmit = {
        ...formData,
        appointment_id: formData.appointment_id || null
      };

      await axios.post('/api/invoices', dataToSubmit);
      setShowModal(false);
      setFormData({
        patient_id: '',
        appointment_id: '',
        treatments: [{
          treatment_code: '',
          description: '',
          cost: 0,
          is_specialist: false
        }],
        payment_method: 'cash',
        paid_amount: 0
      });
      fetchInvoices();
      fetchAppointments();// Refresh to update available appointments

      // Clean up URL if it was used for creating invoice
      if (queryParams.has('new')) {
        navigate('/invoices', { replace: true });
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert(`Failed to create invoice: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCancelInvoice = async () => {
    try {
      await axios.put(`/api/invoices/${selectedInvoice.id}/cancel`, {
        cancellation_reason: cancellationReason
      });
      setShowCancelModal(false);
      setSelectedInvoice(null);
      setCancellationReason('');
      fetchInvoices();
      fetchAppointments();// Refresh to show the appointment as available again
      alert('Invoice cancelled successfully');
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      alert(`Failed to cancel invoice: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      if (!paymentData.amount || paymentData.amount <= 0) {
        alert('Payment amount must be greater than zero');
        return;
      }

      const response = await axios.post(`/api/invoices/${selectedInvoice.id}/payments`, paymentData);
      setShowPaymentModal(false);
      setSelectedInvoice(null);
      setPaymentData({
        amount: 0,
        payment_method: 'cash',
        notes: ''
      });
      fetchInvoices();
      alert('Payment added successfully');
    } catch (error) {
      console.error('Error adding payment:', error);
      alert(`Failed to add payment: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleAddDeduction = async (e) => {
    e.preventDefault();
    try {
      if (!deductionData.deduction_amount || deductionData.deduction_amount <= 0) {
        alert('Deduction amount must be greater than zero');
        return;
      }

      await axios.post(`/api/invoices/${selectedInvoice.id}/manual-deduction`, deductionData);
      setShowDeductionModal(false);
      setSelectedInvoice(null);
      setDeductionData({
        deduction_amount: 0,
        reason: ''
      });
      fetchInvoices();
      alert('Manual deduction added successfully');
    } catch (error) {
      console.error('Error adding deduction:', error);
      alert(`Failed to add deduction: ${error.response?.data?.error || error.message}`);
    }
  };

  const addTreatment = () => {
    setFormData({
      ...formData,
      treatments: [
        ...formData.treatments,
        {
          treatment_code: '',
          description: '',
          cost: 0,
          is_specialist: false
        }
      ]
    });
  };

  const removeTreatment = (index) => {
    if (formData.treatments.length > 1) {
      setFormData({
        ...formData,
        treatments: formData.treatments.filter((_, i) => i !== index)
      });
    }
  };

  const updateTreatment = (index, field, value) => {
    const updatedTreatments = formData.treatments.map((treatment, i) =>
      i === index ? { ...treatment, [field]: value } : treatment
    );
    setFormData({
      ...formData,
      treatments: updatedTreatments
    });
  };

  // Handle treatment code selection from searchable dropdown
  const handleTreatmentSelect = (index, treatment) => {
    console.log('Selected treatment:', treatment);
    const updatedTreatments = formData.treatments.map((t, i) =>
      i === index ? {
        ...t,
        treatment_code: treatment.code,
        description: treatment.description,
        cost: parseFloat(treatment.default_cost) || 0,// Ensure it's a number
        is_specialist: Boolean(treatment.is_specialist)
      } : t
    );
    console.log('Updated treatments:', updatedTreatments);
    setFormData({
      ...formData,
      treatments: updatedTreatments
    });
  };

  const getTotalAmount = () => {
    return formData.treatments.reduce((sum, treatment) => sum + parseFloat(treatment.cost || 0), 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'unpaid': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-grey-100 text-grey-800';
      default: return 'bg-grey-100 text-grey-800';
    }
  };

  const toggleFlag = async (invoiceId, currentFlag) => {
    try {
      await axios.put(`/api/invoices/${invoiceId}/flag`, {
        request_admin_review: !currentFlag
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error toggling flag:', error);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      include_flagged: false
    });
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    const remainingAmount = invoice.total_amount - invoice.paid_amount;
    setPaymentData({
      amount: remainingAmount,
      payment_method: 'cash',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const openDeductionModal = (invoice) => {
    setSelectedInvoice(invoice);
    setDeductionData({
      deduction_amount: 0,
      reason: ''
    });
    setShowDeductionModal(true);
  };

  const openViewModal = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  const openPrintInvoice = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setShowPrintModal(true);
    } catch (error) {
      console.error('Error fetching invoice details for printing:', error);
      alert('Error loading invoice details for printing');
    }
  };

  // Find selected appointment and patient details
  const selectedAppointment = appointments.find(a => a.id == formData.appointment_id);
  const selectedPatient = patients.find(p => p.id == formData.patient_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-grey-900">Invoices</h1>
      </div>

      <div className="bg-white shadow rounded-lg">
        {/* Sticky Search Bar Section */}
        <div className="sticky top-0 z-10 bg-white border-b border-grey-200 shadow-sm">
          <div className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Live Patient Search Bar */}
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-grey-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by patient name, phone, email, QID, or Patient ID..."
                  className="w-full pl-10 pr-10 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                  value={patientSearchTerm}
                  onChange={(e) => setPatientSearchTerm(e.target.value)}
                />
                {patientSearchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey-400 hover:text-grey-600"
                    onClick={clearSearch}
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="bg-grey-200 hover:bg-grey-300 text-grey-800 px-4 py-2 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <SafeIcon icon={FiFilter} className="w-4 h-4" />
                  <span>Filters</span>
                </button>

                {(filters.status || filters.include_flagged) && (
                  <button
                    onClick={clearFilters}
                    className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="mt-2 text-sm text-grey-600">
              {pagination.totalItems} invoice{pagination.totalItems !== 1 ? 's' : ''} found
            </div>

            {showFilters && (
              <div className="mt-4 p-4 bg-grey-50 rounded-lg">
                <h3 className="text-sm font-medium text-grey-700 mb-2">Filters</h3>
                <div className="flex flex-wrap gap-4">
                  <div>
                    <label className="block text-sm font-medium text-grey-700 mb-1">
                      Status
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                      value={filters.status}
                      onChange={(e) => {
                        setFilters({ ...filters, status: e.target.value });
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                      }}
                    >
                      <option value="">All Statuses</option>
                      <option value="paid">Paid</option>
                      <option value="partial">Partial</option>
                      <option value="unpaid">Unpaid</option>
                      {user?.role === 'admin' && <option value="cancelled">Cancelled</option>}
                    </select>
                  </div>

                  {/* Only show flagged filter for admin users */}
                  {user?.role === 'admin' && (
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          className="rounded border-grey-300 text-maroon-600 focus:ring-maroon-500"
                          checked={filters.include_flagged}
                          onChange={(e) => {
                            setFilters({ ...filters, include_flagged: e.target.checked });
                            setPagination(prev => ({ ...prev, currentPage: 1 }));
                          }}
                        />
                        <span className="text-sm text-grey-700">Include flagged transactions</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-grey-200">
            <thead className="bg-grey-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Patient ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Invoice Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Patient
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-grey-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <SafeIcon icon={FiDollarSign} className="w-12 h-12 text-grey-400 mb-4" />
                      <h3 className="text-lg font-medium text-grey-900 mb-2">No invoices found</h3>
                      <p className="text-grey-600 mb-4">
                        {patientSearchTerm
                          ? "No invoices match your search criteria."
                          : filters.status
                            ? `No ${filters.status} invoices found.`
                            : "Create invoices from the appointment page"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  // Find patient to get patient ID
                  const patient = patients.find(p => p.full_name === invoice.patient_name);
                  return (
                    <motion.tr
                      key={invoice.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-grey-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-grey-900">
                          {patient ? `#${formatPatientId(patient.id)}` : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          {!shouldHideInvoiceNumber() && (
                            <div className="text-sm font-medium text-grey-900">
                              Invoice #{invoice.id}
                            </div>
                          )}
                          <div className="text-sm text-grey-500">
                            {format(parseISO(invoice.created_at), 'MMM dd,yyyy')}
                          </div>
                          <div className="flex items-center mt-1">
                            <SafeIcon icon={FiCreditCard} className="w-3 h-3 text-grey-400 mr-1" />
                            <span className="text-xs text-grey-500 capitalize">
                              {invoice.payment_method}
                            </span>
                          </div>
                          {invoice.status === 'cancelled' && (
                            <div className="text-xs text-red-600 mt-1">
                              Cancelled: {invoice.cancellation_reason}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-grey-900">
                          {invoice.patient_name}
                        </div>
                        <div className="text-sm text-grey-500">
                          {invoice.doctor_name && `Dr. ${invoice.doctor_name}`}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-grey-900">
                          <div>Total: QR {invoice.total_amount}</div>
                          <div>Paid: QR {invoice.paid_amount}</div>
                          {invoice.total_amount > invoice.paid_amount && invoice.status !== 'cancelled' && (
                            <div className="text-red-600">
                              Outstanding: QR {invoice.total_amount - invoice.paid_amount}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                        {!!invoice.request_admin_review && (
                          <div className="mt-1">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                              Flagged
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {/* View button - always shown */}
                          <button
                            onClick={() => openViewModal(invoice)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View details"
                          >
                            <SafeIcon icon={FiEye} className="w-4 h-4" />
                          </button>

                          {/* Print button */}
                          <button
                            onClick={() => openPrintInvoice(invoice)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Print invoice"
                          >
                            <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                          </button>

                          {/* Only show payment button if invoice is not cancelled and not fully paid */}
                          {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                            <button
                              onClick={() => openPaymentModal(invoice)}
                              className="text-green-600 hover:text-green-900"
                              title="Add payment"
                            >
                              <SafeIcon icon={FiPlusCircle} className="w-4 h-4" />
                            </button>
                          )}

                          {/* Manual deduction button - for admin and manager on paid invoices with doctor */}
                          {['admin', 'manager'].includes(user?.role) && invoice.doctor_name && (
                            <button
                              onClick={() => openDeductionModal(invoice)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Add manual deduction"
                            >
                              <SafeIcon icon={FiMinus} className="w-4 h-4" />
                            </button>
                          )}

                          {/* Only show flag button if invoice is not cancelled */}
                          {invoice.status !== 'cancelled' && (
                            <button
                              onClick={() => toggleFlag(invoice.id, invoice.request_admin_review)}
                              className={`${invoice.request_admin_review
                                ? 'text-purple-600 hover:text-purple-900'
                                : 'text-grey-400 hover:text-grey-600'}`}
                              title={invoice.request_admin_review ? 'Remove flag' : 'Flag for admin review'}
                            >
                              <SafeIcon icon={FiFlag} className="w-4 h-4" />
                            </button>
                          )}

                          {/* Only show cancel button for admin and if invoice is not already cancelled */}
                          {user?.role === 'admin' && invoice.status !== 'cancelled' && (
                            <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowCancelModal(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Cancel invoice"
                            >
                              <SafeIcon icon={FiX} className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex flex-wrap items-center justify-between">
            <div className="text-sm text-grey-700">
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} invoices
            </div>
            <div className="flex space-x-2 mt-2 sm:mt-0">
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                <SafeIcon icon={FiChevronsLeft} className="w-5 h-5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
              </button>
              <div className="flex items-center px-4">
                <span className="text-grey-700">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
              </div>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <SafeIcon icon={FiChevronsRight} className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Print Invoice Modal */}
      {showPrintModal && selectedInvoice && (
        <InvoicePrint
          invoice={selectedInvoice}
          onClose={() => setShowPrintModal(false)}
        />
      )}

      {/* Manual Deduction Modal */}
      {showDeductionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiMinus} className="w-6 h-6 text-orange-600 mr-2" />
              <h3 className="text-lg font-medium text-grey-900">Add Manual Deduction</h3>
            </div>

            <div className="mb-4">
              <div className="bg-orange-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-orange-800 mb-2">
                  Invoice #{selectedInvoice?.id}
                </h4>
                <p className="text-sm text-orange-700">Patient: {selectedInvoice?.patient_name}</p>
                <p className="text-sm text-orange-700">Doctor: {selectedInvoice?.doctor_name}</p>
                <div className="mt-2 text-sm">
                  <p className="text-orange-700">Total Amount: QR {selectedInvoice?.total_amount}</p>
                </div>
              </div>

              <form onSubmit={handleAddDeduction} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Deduction Amount (QR)
                  </label>
                  <input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={deductionData.deduction_amount}
                    onChange={(e) => setDeductionData({
                      ...deductionData,
                      deduction_amount: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Reason for Deduction
                  </label>
                  <textarea
                    rows={3}
                    required
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={deductionData.reason}
                    onChange={(e) => setDeductionData({
                      ...deductionData,
                      reason: e.target.value
                    })}
                    placeholder="Enter reason for this deduction..."
                  />
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeductionModal(false);
                      setSelectedInvoice(null);
                      setDeductionData({
                        deduction_amount: 0,
                        reason: ''
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    Add Deduction
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-grey-900">
                Invoice #{selectedInvoice.id}
              </h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedInvoice.status)}`}>
                {selectedInvoice.status}
              </span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Patient
                  </label>
                  <p className="text-sm text-grey-900">{selectedInvoice.patient_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Doctor
                  </label>
                  <p className="text-sm text-grey-900">{selectedInvoice.doctor_name || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Date
                  </label>
                  <p className="text-sm text-grey-900">
                    {format(parseISO(selectedInvoice.created_at), 'MMM dd,yyyy')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Payment Method
                  </label>
                  <p className="text-sm text-grey-900 capitalize">{selectedInvoice.payment_method}</p>
                </div>
              </div>

              {selectedInvoice.treatments && selectedInvoice.treatments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Treatments
                  </label>
                  <div className="space-y-2">
                    {selectedInvoice.treatments.map((treatment, index) => (
                      <div key={index} className="bg-grey-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-grey-900">{treatment.description}</p>
                            {treatment.treatment_code && (
                              <p className="text-xs text-grey-500">Code: {treatment.treatment_code}</p>
                            )}
                            {treatment.is_specialist === 1 && (
                              <p className="text-xs text-blue-600">Specialist Treatment</p>
                            )}
                          </div>
                          <p className="text-sm font-medium text-grey-900">QR {treatment.cost}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700">Total Amount</p>
                    <p className="font-medium text-blue-900">QR {selectedInvoice.total_amount}</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Paid Amount</p>
                    <p className="font-medium text-blue-900">QR {selectedInvoice.paid_amount}</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Outstanding</p>
                    <p className="font-medium text-blue-900">
                      QR {selectedInvoice.total_amount - selectedInvoice.paid_amount}
                    </p>
                  </div>
                </div>
              </div>

              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Payment History
                  </label>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((payment, index) => (
                      <div key={index} className="bg-green-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-green-900">
                              QR {payment.amount} - {payment.payment_method}
                            </p>
                            <p className="text-xs text-green-700">
                              {format(parseISO(payment.payment_date), 'MMM dd,yyyy HH:mm')}
                            </p>
                            <p className="text-xs text-green-600">By: {payment.recorded_by_name}</p>
                            {payment.notes && (
                              <p className="text-xs text-green-600 mt-1">{payment.notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedInvoice.manual_deductions && selectedInvoice.manual_deductions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Manual Deductions
                  </label>
                  <div className="space-y-2">
                    {selectedInvoice.manual_deductions.map((deduction, index) => (
                      <div key={deduction.id || index} className="bg-orange-50 p-3 rounded-lg group relative">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-orange-900">
                              QR {deduction.amount} deduction
                            </p>
                            <p className="text-xs text-orange-700">
                              Reason: {deduction.reason}
                            </p>
                            <p className="text-xs text-orange-600">
                              {format(parseISO(deduction.created_at), 'MMM dd,yyyy HH:mm')}
                            </p>
                            <p className="text-xs text-orange-600">By: {deduction.created_by_name}</p>
                          </div>
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => handleDeleteDeduction(deduction.id)}
                              className="ml-2 text-orange-500 hover:text-orange-700 transition-colors"
                              title="Delete deduction"
                            >
                              <FiX className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedInvoice.doctor_payout && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-purple-800 mb-2">Doctor Payout Information</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-purple-700">Gross Amount</p>
                      <p className="font-medium text-purple-900">QR {selectedInvoice.doctor_payout.gross_amount}</p>
                    </div>
                    <div>
                      <p className="text-purple-700">Total Deductions</p>
                      <p className="font-medium text-purple-900">QR {selectedInvoice.doctor_payout.deduction}</p>
                    </div>
                    <div>
                      <p className="text-purple-700">Final Payout</p>
                      <p className="font-medium text-purple-900">QR {selectedInvoice.doctor_payout.payout_amount}</p>
                    </div>
                  </div>
                  {selectedInvoice.doctor_payout.manual_deduction_reason && (
                    <div className="mt-2">
                      <p className="text-xs text-purple-600">
                        Manual deduction applied: {selectedInvoice.doctor_payout.manual_deduction_reason}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedInvoice.status === 'cancelled' && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Cancellation Reason:</p>
                  <p className="text-sm text-red-700">{selectedInvoice.cancellation_reason}</p>
                  {selectedInvoice.cancelled_by_name && (
                    <p className="text-xs text-red-600 mt-1">
                      Cancelled by: {selectedInvoice.cancelled_by_name}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-grey-600 text-white rounded-lg hover:bg-grey-700 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600 mr-2" />
              <h3 className="text-lg font-medium text-grey-900">Add Payment</h3>
            </div>

            <div className="mb-4">
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  Invoice #{selectedInvoice?.id}
                </h4>
                <p className="text-sm text-blue-700">Patient: {selectedInvoice?.patient_name}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-blue-700">Total: QR {selectedInvoice?.total_amount}</p>
                    <p className="text-blue-700">Paid: QR {selectedInvoice?.paid_amount}</p>
                  </div>
                  <div>
                    <p className="font-medium text-blue-800">
                      Outstanding: QR {selectedInvoice?.total_amount - selectedInvoice?.paid_amount}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAddPayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Payment Amount (QR)
                  </label>
                  <input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    max={selectedInvoice?.total_amount - selectedInvoice?.paid_amount}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({
                      ...paymentData,
                      amount: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({
                      ...paymentData,
                      payment_method: e.target.value
                    })}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    rows={2}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({
                      ...paymentData,
                      notes: e.target.value
                    })}
                    placeholder="Any additional information about this payment"
                  />
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedInvoice(null);
                      setPaymentData({
                        amount: 0,
                        payment_method: 'cash',
                        notes: ''
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Record Payment
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cancel Invoice Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-grey-900">Cancel Invoice</h3>
            </div>

            <p className="text-sm text-grey-600 mb-4">
              Are you sure you want to cancel Invoice #{selectedInvoice?.id}?
              This action cannot be undone. The appointment will become available for new invoicing.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-grey-700 mb-1">
                Cancellation Reason
              </label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedInvoice(null);
                  setCancellationReason('');
                }}
                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
              >
                Keep Invoice
              </button>
              <button
                onClick={handleCancelInvoice}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Cancel Invoice
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create Invoice Modal - Only shown when coming from appointment page */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto"
          >
            <h3 className="text-lg font-medium text-grey-900 mb-4">Create New Invoice</h3>

            {/* Show appointment details if coming from appointment */}
            {selectedAppointment && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                <div className="font-medium">Creating invoice for appointment:</div>
                <div>Patient: {selectedAppointment.patient_name}</div>
                <div>Doctor: {selectedAppointment.doctor_name}</div>
                <div>Date: {format(parseISO(selectedAppointment.datetime_start), 'MMM dd,yyyy HH:mm')}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Patient
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.patient_id}
                    onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                    disabled={queryParams.has('patient_id') || queryParams.has('appointment_id')}
                  >
                    <option value="">Select Patient</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Appointment (Optional)
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.appointment_id}
                    onChange={(e) => setFormData({ ...formData, appointment_id: e.target.value })}
                    disabled={queryParams.has('appointment_id')}
                  >
                    <option value="">Select Appointment</option>
                    {appointments
                      .filter(appointment => !formData.patient_id || appointment.patient_id == formData.patient_id)
                      .map((appointment) => (
                        <option key={appointment.id} value={appointment.id}>
                          {appointment.patient_name} - {format(parseISO(appointment.datetime_start), 'MMM dd,yyyy')}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-grey-500 mt-1">
                    Only completed appointments without active invoices are shown
                  </p>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-grey-700">
                    Treatments
                  </label>
                  <button
                    type="button"
                    onClick={addTreatment}
                    className="text-maroon-600 hover:text-maroon-700 text-sm"
                  >
                    + Add Treatment
                  </button>
                </div>

                {formData.treatments.map((treatment, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-3 p-3 bg-grey-50 rounded-lg">
                    <div className="col-span-4">
                      <label className="block text-xs text-grey-500 mb-1">Treatment Code</label>
                      <SearchableDropdown
                        value={treatment.treatment_code}
                        onChange={(value) => updateTreatment(index, 'treatment_code', value)}
                        onSelect={(selectedTreatment) => handleTreatmentSelect(index, selectedTreatment)}
                        placeholder="Search treatments..."
                        treatmentCodes={treatmentCodes}
                      />
                    </div>

                    <div className="col-span-4">
                      <label className="block text-xs text-grey-500 mb-1">Description</label>
                      <input
                        type="text"
                        placeholder="Description"
                        readOnly
                        className="w-full px-3 py-2 border border-grey-300 rounded-lg bg-grey-100 cursor-help"
                        value={treatment.description}
                        title={treatment.description}// Add tooltip showing full description
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-xs text-grey-500 mb-1">Cost</label>
                      <input
                        type="number"
                        placeholder="Cost"
                        required
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                        value={treatment.cost}
                        onChange={(e) => updateTreatment(index, 'cost', parseFloat(e.target.value) || 0)}
                      />
                    </div>

                    <div className="col-span-1 flex items-end">
                      {formData.treatments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTreatment(index)}
                          className="w-full h-10 flex items-center justify-center text-red-600 hover:text-red-700"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {isDoctorSpecialist && (
                      <div className="col-span-12 mt-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            className="rounded border-grey-300 text-maroon-600 focus:ring-maroon-500"
                            checked={treatment.is_specialist}
                            onChange={(e) => updateTreatment(index, 'is_specialist', e.target.checked)}
                          />
                          <span className="text-sm text-grey-700">Is Specialist Treatment?</span>
                        </label>
                      </div>
                    )}
                  </div>
                ))}

                <div className="text-right text-lg font-medium text-grey-900 mt-2">
                  Total: QR {getTotalAmount().toFixed(2)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Paid Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    max={getTotalAmount()}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.paid_amount}
                    onChange={(e) => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    // Clean up URL if it was used for creating invoice
                    if (queryParams.has('new')) {
                      navigate('/invoices', { replace: true });
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-maroon-600 text-white rounded-lg hover:bg-maroon-700 transition-colors"
                >
                  Create Invoice
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Invoices;